import { Card, CardContent, Typography, Divider, Alert } from '@mui/material';
import { MapWithMarker } from 'components/data/MapWithMarker/MapWithMarker';
import DataLabelPairing from 'components/DataLabelPairing/DataLabelPairing';
import { FormSection } from 'components/form';
import {
    FieldError,
    FieldErrorsImpl,
    Merge,
    useFormContext,
} from 'react-hook-form';
import { PhysicalDeviceInput } from 'types';
import styles from './Summary.module.scss';
import { useEffect } from 'react';
import { useFormState } from 'hooks/useFormState';

interface CreatePhysicalDeviceModalSummarySectionProps {
    shortDeviceId?: string;
}

export function CreatePhysicalDeviceModalSummarySection({
    shortDeviceId,
}: CreatePhysicalDeviceModalSummarySectionProps) {
    const {
        watch,
        formState: { errors },
    } = useFormContext();
    const device = watch() as PhysicalDeviceInput;

    const formSectionTitle = 'Summary';

    const { setIsFormSubmitDisabled, currentFormSectionIndex, formSections } =
        useFormState();

    useEffect(() => {
        if (
            currentFormSectionIndex === formSections.indexOf(formSectionTitle)
        ) {
            setIsFormSubmitDisabled(false);
        }
    }, [setIsFormSubmitDisabled, currentFormSectionIndex, formSections]);

    // Type guard to check if error is a FieldError
    const isFieldError = (error: any): error is FieldError => {
        return error && typeof error.message === 'string';
    };

    // Function to render the error message
    const renderErrorMessage = (
        error: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined
    ) => {
        if (isFieldError(error)) {
            return error.message;
        }
        // Handle other types of errors or provide a generic message
        return 'Invalid field';
    };

    // Check for errors immediately when the component is rendered
    const hasErrors = Object.keys(errors).length > 0;

    return (
        <FormSection title={formSectionTitle}>
            {/* Displaying validation errors */}
            {hasErrors && (
                <Alert severity="error">
                    <Typography>
                        Please correct the following errors:
                    </Typography>
                    <ul>
                        {Object.entries(errors).map(([key, error]) => (
                            <li key={key}>{renderErrorMessage(error)}</li>
                        ))}
                    </ul>
                </Alert>
            )}

            <Card className={styles.details}>
                <CardContent>
                    <Typography variant="h5">
                        Verify Monitor Deployment Info{' '}
                    </Typography>
                    <Divider textAlign="left">Monitor Information</Divider>
                    <DataLabelPairing label="Name" data={device.name} />
                    <DataLabelPairing
                        label="Short Device Id"
                        data={shortDeviceId ?? 'N/A'}
                    />
                    <DataLabelPairing label="Address" data={device.address} />
                    <MapWithMarker address={device.address} />
                    <Divider textAlign="left">Customer Information</Divider>
                    <DataLabelPairing
                        label="Customer Name"
                        data={device.customer_name}
                    />
                    <Divider textAlign="left">Tank Information</Divider>
                    <DataLabelPairing
                        label="Fuel Type"
                        data={device.fuelType}
                    />
                    <DataLabelPairing
                        label="Capacity (Gal)"
                        data={device.capacity}
                    />
                    <DataLabelPairing
                        label="Orientation"
                        data={device.orientation}
                    />
                </CardContent>
            </Card>
        </FormSection>
    );
}
